import React from 'react';
import { MetamodelViewState } from './types';
import { MetamodelViewCommands } from './metamodelViewCommands';
import {
  DoqContainer,
  EnforceContentWidth,
  FieldType,
  List,
  SidebarHeader,
  WorkspaceAndFieldItem,
} from '../components/atoms';
import { CustomClosableIslandHeader } from 'viewpointBuilder/components/CustomClosableIslandHeader';
import { SidebarMenu } from '@ardoq/sidebar-menu';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { ControlledCollapsibleIsland } from '@ardoq/page-layout';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';

type MetamodelSidePanelProps = {
  state: MetamodelViewState;
  commands: MetamodelViewCommands;
  i18nLocale: I18nLocale;
};
export const MetamodelSidePanel = ({
  state: {
    expandedSection,
    selectedFields,
    selectedWorkspaces,
    selectedComponentType,
  },
  commands,
  i18nLocale,
}: MetamodelSidePanelProps) => {
  if (!selectedComponentType) {
    return (
      <FlexBox
        height="full"
        width="full"
        gap="medium"
        backgroundColor="surfaceDefault"
        justify="center"
      >
        <DoqContainer>
          <ResponsiveDoqWithSpeechBubble
            doqType={DoqType.SIMPLE}
            title={t(
              i18nLocale,
              'viewpointBuilder:metamodel.noSelection.title',
              'Select a component type'
            )}
            message={t(
              i18nLocale,
              'viewpointBuilder:metamodel.noSelection.message',
              'Select a component type to see workspaces and fields it is used in.'
            )}
          />
        </DoqContainer>
      </FlexBox>
    );
  }
  return (
    <SidebarMenu
      isPageBodyMenu
      isResizable
      isRightContent
      headerBody={
        <SidebarHeader
          type="component"
          selectedType={selectedComponentType}
          headerText={t(
            i18nLocale,
            'viewpointBuilder:metamodel.header',
            'Workspaces and fields for:'
          )}
        />
      }
    >
      <EnforceContentWidth>
        <Box padding="medium">
          <Stack gap="medium">
            <ControlledCollapsibleIsland
              minWidth="300"
              bodySpacing="medium"
              isExpanded={expandedSection.workspaces}
              onToggle={() => commands.toggleExpandableSection('workspaces')}
              header={
                <CustomClosableIslandHeader
                  label={t(
                    i18nLocale,
                    'viewpointBuilder:metamodel.workspaces',
                    'Workspaces'
                  )}
                  isExpanded={expandedSection.workspaces}
                  onToggle={() =>
                    commands.toggleExpandableSection('workspaces')
                  }
                  i18nLocale={i18nLocale}
                />
              }
            >
              {selectedWorkspaces && (
                <List>
                  {selectedWorkspaces.map((name, index) => (
                    <WorkspaceAndFieldItem key={index} $isClickable={false}>
                      {name}
                    </WorkspaceAndFieldItem>
                  ))}
                </List>
              )}
            </ControlledCollapsibleIsland>

            <ControlledCollapsibleIsland
              minWidth="300"
              bodySpacing="medium"
              isExpanded={expandedSection.fields}
              onToggle={() => commands.toggleExpandableSection('fields')}
              header={
                <CustomClosableIslandHeader
                  label={t(
                    i18nLocale,
                    'viewpointBuilder:metamodel.fields',
                    'Fields'
                  )}
                  isExpanded={expandedSection.fields}
                  onToggle={() => commands.toggleExpandableSection('fields')}
                  i18nLocale={i18nLocale}
                />
              }
            >
              {selectedFields && (
                <List>
                  {selectedFields.map(({ label, type }, index) => (
                    <WorkspaceAndFieldItem key={index} $isClickable={false}>
                      <span>{label}</span>
                      <FieldType>{type}</FieldType>
                    </WorkspaceAndFieldItem>
                  ))}
                </List>
              )}
            </ControlledCollapsibleIsland>
          </Stack>
        </Box>
      </EnforceContentWidth>
    </SidebarMenu>
  );
};
